/**
 * A club's own player fields, named from whatever its spreadsheet calls them.
 *
 * Shared because the importer must show the coach the same key, kind and
 * sensitivity marking that the server will store — a preview that disagrees
 * with the result is worse than no preview.
 */

/**
 * Turns a spreadsheet heading into a stable key.
 *
 * "Parent's phone" and "PARENT'S PHONE " must land on the same field, or the
 * second import creates a duplicate column nobody asked for.
 */
export function fieldKey(label: string): string {
  const base = label
    .normalize('NFD')
    // Latin accents and Arabic harakat both come off: a coach types "Numero"
    // and "Numéro", or writes a heading with and without vowel marks, and
    // both have to reach the same column.
    .replace(/[\u0300-\u036f\u064b-\u0652\u0670]/g, '')
    // Back to composed form, so an Arabic letter that came apart during the
    // pass is whole again and two imports of the same sheet agree.
    .normalize('NFC')
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06ff]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return base || 'field';
}

/**
 * Fields that hold a way to contact somebody.
 *
 * Marked on sight, because most of these players are minors and a guardian's
 * phone number should not appear on a printed team sheet by accident. It changes
 * how the field is shown, not whether it is stored — the club decides what it
 * keeps.
 */
const CONTACT_HINTS =
  /(phone|tel|mobile|gsm|whatsapp|email|mail|contact|parent|guardian|tuteur|père|pere|mère|mere|papa|maman|address|adresse|cin|passport|passeport|licence|license|هاتف|جوال|بريد|عنوان|ولي|الأب|الأم)/i;

export function looksSensitive(label: string): boolean {
  return CONTACT_HINTS.test(label);
}

export function guessKind(label: string): string {
  if (/(email|mail|بريد)/i.test(label)) return 'email';
  if (/(phone|tel|mobile|gsm|whatsapp|هاتف|جوال)/i.test(label)) return 'phone';
  if (/(date|birth|naissance|تاريخ)/i.test(label)) return 'date';
  if (/(number|numéro|numero|size|taille|poids|weight|رقم)/i.test(label)) return 'number';
  return 'text';
}
